import { Trans } from '@lingui/react'
import classnames from 'clsx'
import { arrayOf, type InferProps, number } from 'prop-types'
import { type VFC } from 'react'

import { DOCUMENT_TYPE, TENANT_IT } from 'shared/consts'
import checkmarkIconRebranding from 'views/assets/images/icons/check-mark-rebranding.svg'
import Image from 'views/components/atoms/Image'
import FreeShippingProgressBar from 'views/components/organisms/FreeShippingIndicator/FreeShippingProgressBar'
import { getPriceToPrint } from 'views/components/organisms/FreeShippingIndicator/helpers'
import { isSellerShopApotheke } from 'views/components/utils/isSellerShopApotheke'
import Tenant from 'views/components/utils/Tenant'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import { cartShippingDetail, sellerGroup } from 'views/utils/propTypes'

import './FreeShippingIndicatorAddressBox.scss'

const FreeShippingIndicatorAddressBoxProps = {
  idx: number.isRequired,
  sellerGroups: arrayOf(sellerGroup),
  shippingDetail: arrayOf(cartShippingDetail),
}

type PropTypes = InferProps<typeof FreeShippingIndicatorAddressBoxProps>

const FreeShippingIndicatorAddressBox: VFC<Partial<PropTypes>> = ({
  idx,
  shippingDetail,
  sellerGroups,
}) => {
  const {
    publicRuntimeConfig: {
      publicConfig: { tenant },
    },
  } = useShopAndRuntimeConfig()

  const seller = sellerGroups?.[idx!]
  const shippingDetailForSeller = shippingDetail?.[idx!]

  const freeShippingThreshold = shippingDetailForSeller?.freeShippingThreshold
  const shippingCost = shippingDetailForSeller?.shippingCost
  const initialShippingCost = shippingDetailForSeller?.initialShippingCost
  const totalAppliedDiscounts = seller?.totalAppliedDiscounts ?? 0
  const subAmount = seller?.subAmount ?? 0
  const totalAmountWithoutShipping = subAmount - totalAppliedDiscounts

  const isSaProduct = isSellerShopApotheke(seller?.sellerId || '')

  if (
    !freeShippingThreshold ||
    shippingCost === null ||
    shippingCost === undefined ||
    initialShippingCost === null ||
    initialShippingCost === undefined ||
    !totalAmountWithoutShipping
  ) {
    return null
  }

  const isFreeShipping = shippingCost === 0
  const isRxProductInCart = seller?.groupEntries.some(entry =>
    [
      DOCUMENT_TYPE.INSURANCE_PRESCRIPTION,
      DOCUMENT_TYPE.FREE_PRESCRIPTION,
      DOCUMENT_TYPE.PRIVATE_PRESCRIPTION,
    ].includes(entry?.type as DOCUMENT_TYPE)
  )

  const missingAmountToGetFreeShipping = isFreeShipping
    ? 0
    : Math.abs(freeShippingThreshold - totalAmountWithoutShipping)

  const freeShippingItem =
    isRxProductInCart && tenant !== TENANT_IT ? (
      <Trans id="freeShippingIndicator.label.freeShipping" />
    ) : (
      <>
        <Image
          className="o-FreeShippingAddressBox__free-shipping-checkmark"
          src={checkmarkIconRebranding}
          alt="checkmark"
        />
        <Trans id="freeShippingIndicator.label.freeShipping" />
        <Tenant tenant={[TENANT_IT]}>
          {() =>
            isSaProduct ? (
              <span className="o-FreeShippingAddressBox__initial-shipping-cost">
                {getPriceToPrint(initialShippingCost, tenant)}
              </span>
            ) : (
              <span className="u-padding-xx-small"></span>
            )
          }
        </Tenant>
        <Tenant not tenant={[TENANT_IT]}>
          {() => (
            <span className="o-FreeShippingAddressBox__initial-shipping-cost">
              {getPriceToPrint(initialShippingCost, tenant)}
            </span>
          )}
        </Tenant>
        <span>{getPriceToPrint(shippingCost, tenant)}</span>
      </>
    )

  const missingAmountItem = (
    <Trans
      id="freeShippingIndicator.label.missingAmount"
      values={{
        missingAmount: getPriceToPrint(missingAmountToGetFreeShipping, tenant),
      }}
    />
  )

  return (
    <div
      className={classnames(
        'l-flex l-flex--start@small-tiny l-flex__column_reverse@small-tiny u-color--blue u-font-weight--bold u-margin--right@medium u-padding-small--ends u-no-padding--ends@small-tiny o-FreeShippingAddressBox__missing-amount-box',

        {
          'o-FreeShippingAddressBox__free-shipping-box': isFreeShipping,
        }
      )}
    >
      {isRxProductInCart && (
        <span
          className={classnames(
            'u-margin--right o-FreeShippingAddressBox__positive-free-shipping',
            {
              'u-color--positive l-flex': isFreeShipping,
            }
          )}
        >
          {isFreeShipping ? freeShippingItem : missingAmountItem}
        </span>
      )}

      {!isRxProductInCart && freeShippingThreshold > 0 && (
        <FreeShippingProgressBar
          isFreeShipping={isFreeShipping}
          currentAmount={totalAmountWithoutShipping}
          freeShippingThreshold={freeShippingThreshold}
          shippingCost={shippingCost}
          initialShippingCost={initialShippingCost}
          missingAmountItem={missingAmountItem}
          tenant={tenant}
        />
      )}
    </div>
  )
}

export default FreeShippingIndicatorAddressBox
